import { Card, Col, Container, Form, Row } from "react-bootstrap";
import { identificacionDocumento } from "./tradicion";
import "./Tarjetax.css";

const CLASS_UI = {
  PADRE: { mod: "tx-padre", label: "Padre" },
  HIJO: { mod: "tx-hijo", label: "Hijo" },
  OTRO: { mod: "tx-otro", label: "Otro" },
};

/** Cantidad de metadatos que toda tarjeta muestra, tenga los que tenga */
const METADATOS_VISIBLES = 4;

/**
 * Metadatos que la tarjeta prefiere, en este orden, cuando el cargue los tiene
 * dentro de su estructura. El tercer lugar es para la identificacion del
 * documento (FACTURA / DOCUMENTO DE TRANSPORTE / NUMERO BL) y lo que falte se
 * completa siguiendo el orden de la propia estructura.
 */
const METADATOS_FIJOS = ["CLIIDXXX", "DOCNROXX"];

/**
 * Metadatos que nunca se usan para rellenar: la tradicion documental se saco
 * de la tarjeta a peticion del area, y las otras tres claves son las candidatas
 * a identificacion, que ya tienen su propia fila.
 */
const METADATOS_EXCLUIDOS = ["TRADOCXX", "TRADOCUX", "FACOMERX", "DOCTRANS", "NUMBLXXX"];

const aTexto = (valor) =>
  Array.isArray(valor)
    ? valor.map((v) => String(v ?? "").trim()).filter(Boolean).join(", ")
    : String(valor ?? "").trim();

/**
 * Metadatos que SI pertenecen a la estructura del cargue.
 *
 * METADATX ya viene filtrado por el backend con el join a GEST0008 sobre el
 * area, la serie y el tipo documental del cargue, y ordenado por GEST0008
 * .ORDENXXX, asi que es la definicion misma de "metadato de la estructura".
 * Llega indexado por nombre (METDESXX), por eso se cruza con METAKEYS para
 * recuperar la clave de cada uno.
 *
 * Lo que queda fuera es justo lo que no se debe pintar: los campos que el
 * gestor escribe solo en JSONMETX y no son metadatos documentales (CATIDXXX,
 * la categoria del cliente; ODIIDXXX; TRABANVE...).
 *
 * @param {object} row
 * @returns {Array<{clave: string|null, etiqueta: string, valor: string}>}
 */
const estructuraDelCargue = (row) => {
  const estructura = row?.METADATX || {};
  const claveDe = {};
  Object.entries(row?.METAKEYS || {}).forEach(([clave, etiqueta]) => {
    if (etiqueta && !(etiqueta in claveDe)) claveDe[etiqueta] = clave;
  });
  return Object.entries(estructura).map(([etiqueta, valor]) => ({
    clave: claveDe[etiqueta] || null,
    etiqueta,
    valor: aTexto(valor),
  }));
};

/**
 * Arma las METADATOS_VISIBLES filas de metadatos de la tarjeta: siempre la
 * misma cantidad y en el mismo orden, para que todas las tarjetas midan igual.
 * Solo entran metadatos de la estructura del cargue (GEST0008). Las filas que
 * sobran quedan vacias en lugar de desaparecer, y ninguna etiqueta se inventa:
 * son los nombres de GEST0004 que llegan en METADATX.
 *
 * @param {object} row
 * @param {{clave: string|null, valor: string}} ident
 * @returns {Array<{clave: string|null, etiqueta: string, valor: string}>}
 */
const metadatosTarjeta = (row, ident) => {
  const estructura = estructuraDelCargue(row);
  const porClave = new Map(estructura.filter((d) => d.clave).map((d) => [d.clave, d]));

  const filas = [];
  const agregadas = new Set();

  // Primero los preferidos, y solo si el cargue los tiene en su estructura. La
  // clave de identificacion entra aqui aunque este en METADATOS_EXCLUIDOS: esa
  // lista solo aplica al relleno, para que no se repita en otra fila.
  [...METADATOS_FIJOS, ident.clave].forEach((clave) => {
    if (!clave || agregadas.has(clave)) return;
    const dato = porClave.get(clave);
    if (!dato) return;
    agregadas.add(clave);
    filas.push(dato);
  });

  // El resto se completa en el orden de la estructura (GEST0008.ORDENXXX)
  estructura.forEach((dato) => {
    if (filas.length >= METADATOS_VISIBLES) return;
    if (!dato.valor) return;
    if (dato.clave && (agregadas.has(dato.clave) || METADATOS_EXCLUIDOS.includes(dato.clave))) {
      return;
    }
    if (dato.clave) agregadas.add(dato.clave);
    filas.push(dato);
  });

  return filas
    .slice(0, METADATOS_VISIBLES)
    .concat(
      Array.from({ length: Math.max(0, METADATOS_VISIBLES - filas.length) }, () => ({
        clave: null,
        etiqueta: "",
        valor: "",
      }))
    );
};

const Tarjetax = ({
  row,
  idx,
  clase,
  checked,
  onCheck,
  viewLine,
  viewMetada,
  viewFiles,
  viewImages,
  onAsociarPadre,
  versiones = [],
  onVerVersiones,
}) => {
  const ui = CLASS_UI[clase] || CLASS_UI.OTRO;
  const esImagen =
    row.TYPEXXXX === "image/png" ||
    row.TYPEXXXX === "image/jpeg" ||
    row.TYPEXXXX === "image/jpg";
  const esInvalido = row.REGESTXX === "INVALIDO";
  // Cargues de tradicion documental de jerarquia menor que oculta esta tarjeta
  const tieneVersiones = versiones.length > 0 && !!onVerVersiones;

  // Datos de la tarjeta. Solo se pintan metadatos de la estructura del cargue
  // (GEST0008) y con el nombre que les dio GEST0004, tal como llegan en
  // METADATX. Ninguna etiqueta se inventa ni se muestra una clave cruda.
  const ident = identificacionDocumento(row);
  const metadatos = metadatosTarjeta(row, ident);

  return (
    <Card className={`shadow rounded p-3 tx-card ${ui.mod} ${esInvalido ? "tx-inv" : ""}`}>
      <span className={`tx-badge ${ui.mod}`}>{ui.label}</span>
      <Container fluid>
        <Row>
          <Col>
            {esImagen ? (
              <img
                variant="top"
                src={`${process.env.REACT_APP_GESTOR_WEB}/${row.imgfilex}`}
                width="25"
                height="35"
                alt="imagen-files"
              />
            ) : (
              <img
                variant="top"
                src={`${process.env.REACT_APP_GESTOR_WEB}/${row.imgfilex}`}
                width="50"
                alt="imagen-files"
              />
            )}
          </Col>
          <Col className="d-grid justify-content-end">
            <Form.Check
              value={`${row.IDARCHXX}`}
              onChange={(e) => onCheck(idx, e)}
              checked={!!checked}
              disabled={esInvalido}
            />
          </Col>
        </Row>
        <Row>
          <Card.Body>
            <Container className="tx-datos">
              {/*
                Estructura FIJA: tipo documental, CUATRO metadatos y fecha de
                cargue, siempre en el mismo orden y con el mismo formato de
                siempre (ETIQUETA : valor), para que todas las tarjetas midan
                igual. Lo unico que cambia frente al original es que el valor ya
                no se corta a 30 caracteres: ahora se muestra completo, pasando a
                la linea siguiente si hace falta.
              */}
              <Row>
                <Col className="col-12 text-doc p-0 tx-dato">
                  <strong>TIPO</strong> : {row.TIPODESX}
                </Col>
              </Row>
              {metadatos.map((dato, i) => (
                <Row key={dato.clave || dato.etiqueta || `vacio-${i}`}>
                  <Col className="col-12 text-doc p-0 tx-dato">
                    {dato.etiqueta ? (
                      <>
                        <strong>{dato.etiqueta}</strong> : {dato.valor}
                      </>
                    ) : (
                      // Fila vacia: conserva el alto de la tarjeta cuando la
                      // estructura del cargue trae menos metadatos que filas.
                      <>&nbsp;</>
                    )}
                  </Col>
                </Row>
              ))}
              <Row>
                <Col className="col-12 text-doc p-0 tx-dato">
                  <strong>FECHA</strong> : {row.REGFECXX} / {row.REGHORXX}
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Row>
        <Row className="tx-acciones">
          <Col>
            <img
              onClick={() => viewLine(row)}
              src={`${process.env.REACT_APP_GESTOR_WEB}/images/icon-timeline.png`}
              height={30}
              alt="time line"
              style={{ cursor: "pointer" }}
            />
          </Col>
          <Col>
            <img
              onClick={() => viewMetada(row.METADATX, row.JSONMETX, row.METAEXCE)}
              variant="top"
              src={`${process.env.REACT_APP_GESTOR_WEB}/images/icon-info.png`}
              height={30}
              alt="info"
              style={{ cursor: "pointer" }}
            />
          </Col>
          <Col>
            <img
              onClick={() => (esImagen ? viewImages(row) : viewFiles(row))}
              variant="top"
              src={`${process.env.REACT_APP_GESTOR_WEB}/images/icon-view.png`}
              height={30}
              alt="view"
              style={{ cursor: "pointer" }}
            />
          </Col>
          {tieneVersiones && (
            <Col>
              <button
                type="button"
                className="tx-versiones"
                onClick={() => onVerVersiones(row, versiones)}
                title={`Ver ${versiones.length} tradicion${
                  versiones.length > 1 ? "es" : ""
                } documental${versiones.length > 1 ? "es" : ""} de jerarquia menor`}
              >
                <i className="bi bi-layers-half"></i>
                <span className="tx-versiones__n">{versiones.length}</span>
              </button>
            </Col>
          )}
        </Row>
        {clase === "HIJO" && !esInvalido && onAsociarPadre && (
          <Row>
            <Col className="p-0">
              <button
                type="button"
                className="tx-asociar-padre"
                onClick={() => onAsociarPadre(row)}
                title="Asociar otro documento padre"
              >
                + Asociar otro padre
              </button>
            </Col>
          </Row>
        )}
      </Container>
    </Card>
  );
};

export default Tarjetax;
